'use client';

import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { authClient } from '@/lib/auth/client';
import { loginSchema, type LoginInput } from '../types/login-schema';
import { PasswordField } from './password-field';
import { adminLogin } from '../actions/admin-login';

export function AdminLoginForm() {
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasHandledReason = useRef(false);

  // Handle ?reason= URL param toasts on mount
  useEffect(() => {
    if (hasHandledReason.current) return;
    hasHandledReason.current = true;

    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');

    if (reason === 'idle') {
      toast.info('You were signed out due to inactivity.', {
        duration: 6000,
      });
      params.delete('reason');
      const newSearch = params.toString();
      const newUrl =
        window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState(null, '', newUrl);
    } else if (reason === 'session_expired') {
      toast.info('Your session has expired. Please sign in again.', {
        duration: 6000,
      });
      params.delete('reason');
      const newSearch = params.toString();
      const newUrl =
        window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState(null, '', newUrl);
    }
  }, []);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    resetField,
  } = form;

  const onSubmit = async (data: LoginInput) => {
    setFormError(null);
    setIsSubmitting(true);

    try {
      // Step 1: Attempt sign-in via better-auth client (sets browser session cookie)
      const result = await authClient.signIn.email({
        email: data.email,
        password: data.password,
        callbackURL: '/dashboard',
      });

      if (result.error) {
        resetField('password');
        setFormError(
          'The email or password you entered is incorrect. Please try again.',
        );
        setTimeout(() => {
          const passwordInput = document.querySelector<HTMLInputElement>(
            'input[autocomplete="current-password"]',
          );
          passwordInput?.focus();
        }, 0);
        return;
      }

      // Step 2: Run org active check + write LOGIN_SUCCESS audit log via server action
      const auditResult = await adminLogin({ email: data.email, password: data.password });
      if (auditResult?.data?.success === false) {
        // Org inactive or banned — sign out immediately and show generic error
        await authClient.signOut();
        resetField('password');
        setFormError(
          'The email or password you entered is incorrect. Please try again.',
        );
        setTimeout(() => {
          const passwordInput = document.querySelector<HTMLInputElement>(
            'input[autocomplete="current-password"]',
          );
          passwordInput?.focus();
        }, 0);
        return;
      }

      // Success — redirect to briefing
      window.location.href = '/dashboard';
    } catch {
      setFormError(
        'Unable to connect. Check your internet connection and try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build a controller-compatible field object for PasswordField
  const passwordField = {
    name: 'password' as const,
    ref: form.register('password').ref,
    value: watch('password'),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setValue('password', e.target.value, { shouldValidate: false }),
    onBlur: () => {},
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* Card heading */}
      <h1 className="mb-1 text-xl font-semibold text-foreground">Sign in</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Enter your credentials to access your organisation&apos;s data
      </p>

      {/* Email field */}
      <div className="mb-4 flex flex-col gap-1.5">
        <label className="text-sm font-medium text-foreground">Email</label>
        <input
          {...register('email')}
          type="email"
          autoComplete="email"
          disabled={isSubmitting}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const passwordInput = document.querySelector<HTMLInputElement>(
                'input[autocomplete="current-password"]',
              );
              passwordInput?.focus();
            }
          }}
          className={cn(
            'h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:border-ring',
            errors.email && 'border-destructive focus:ring-destructive',
            isSubmitting && 'cursor-not-allowed opacity-50',
          )}
          placeholder="you@company.com"
        />
        {errors.email && (
          <p className="text-xs text-destructive">{errors.email.message}</p>
        )}
      </div>

      {/* Password field */}
      <PasswordField
        field={passwordField}
        label="Password"
        disabled={isSubmitting}
        error={errors.password?.message}
      />

      {/* Form-level error */}
      {formError && (
        <p className="mt-3 text-sm text-destructive">{formError}</p>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className={cn(
          'mt-6 flex h-10 w-full items-center justify-center rounded-md bg-primary text-sm font-medium text-primary-foreground transition-opacity',
          isSubmitting && 'cursor-not-allowed opacity-50',
        )}
      >
        {isSubmitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          'Sign in'
        )}
      </button>
    </form>
  );
}
